import React from 'react';
import { SelectOption } from 'global';
import { Box, Stack } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { FormSectionHeader, FormSectionHeaderProps } from 'components/shared/FormSectionHeader';
import { equipmentConditions, equipmentYears, makeOptions, classOptions } from 'modules/equipment';
import { AutocompleteValueField, CurrencyInputField, FormInputSelect, InputField, SelectField } from 'components/form';

import { DeskingEquipmentFormPart } from './types';
import { listOfEquipmentDeskingFormFieldsDependOnEntity } from './constants';

interface Props<Value extends string> extends FormSectionHeaderProps<Value> {
  equipments: any[];
  isSectionDisabled?: boolean;
  equipmentOptions: SelectOption[];
  onChangeEquipment?: (equipmentId: string) => void;
}

export const DeskingEquipmentFormSection = <Value extends string>({
  equipments,
  equipmentOptions,
  onChangeEquipment,
  isSectionDisabled = false,
  ...headerProps
}: Props<Value>) => {
  const { control, setValue } = useFormContext<DeskingEquipmentFormPart>();

  const handleChangeEquipment = async (equipmentId: string) => {
    const selectedEquipment = equipments?.find(equipment => equipment.equipmentId === equipmentId);

    setValue('equipment.selectedEquipment', equipmentId);

    listOfEquipmentDeskingFormFieldsDependOnEntity.forEach(field =>
      setValue(`equipment.${field}`, selectedEquipment?.[field] ?? '')
    );

    onChangeEquipment?.(equipmentId);
  };

  return (
    <Stack className="section">
      <FormSectionHeader {...headerProps} />

      <RenderWithCondition condition={!!headerProps.isSectionOpen}>
        <Stack gap="1rem">
          <Box className="row">
            <Controller
              control={control}
              name="equipment.selectedEquipment"
              render={({ field }) => (
                <FormInputSelect
                  size="small"
                  variant="standard"
                  label="Select Equipment"
                  options={equipmentOptions}
                  {...field}
                  onChange={handleChangeEquipment}
                />
              )}
            />

            <Box />
          </Box>

          <Box className="row">
            <InputField
              label="Quantity"
              control={control}
              name="equipment.quantity"
              inputProps={{ disabled: isSectionDisabled }}
            />

            <AutocompleteValueField
              control={control}
              name="equipment.year"
              options={equipmentYears}
              textFieldProps={{ label: 'Year', size: 'small', disabled: isSectionDisabled }}
            />

            <SelectField
              label="Make"
              control={control}
              name="equipment.make"
              options={makeOptions}
              disabled={isSectionDisabled}
            />

            <InputField
              label="Model"
              control={control}
              name="equipment.model"
              inputProps={{ disabled: isSectionDisabled }}
            />

            <SelectField
              label="Class"
              control={control}
              name="equipment.class"
              options={classOptions}
              disabled={isSectionDisabled}
            />

            <InputField
              control={control}
              label="Serial/VIN#"
              name="equipment.serialNumberVin"
              inputProps={{ disabled: isSectionDisabled }}
            />
          </Box>

          <Box className="row">
            <InputField
              label="Stock Id"
              control={control}
              name="equipment.stockId"
              inputProps={{ disabled: isSectionDisabled }}
            />

            <SelectField
              label="Condition"
              control={control}
              name="equipment.newUsed"
              options={equipmentConditions}
              inputProps={{ disabled: isSectionDisabled }}
            />

            <InputField
              label="Mileage"
              control={control}
              name="equipment.mileage"
              inputProps={{ disabled: isSectionDisabled }}
            />

            <InputField
              control={control}
              label="Description"
              name="equipment.description"
              inputProps={{ disabled: isSectionDisabled }}
            />

            <CurrencyInputField
              control={control}
              label="Dealer Cost"
              name="equipment.totalWholesaleCost"
              textFieldProps={{ disabled: isSectionDisabled }}
            />

            <CurrencyInputField
              label="Sell Price"
              control={control}
              name="equipment.retailPrice"
              textFieldProps={{ disabled: isSectionDisabled }}
            />
          </Box>
        </Stack>
      </RenderWithCondition>
    </Stack>
  );
};
